import { RuleEngine } from '@lio/rule-engine/rules/engine';
import { validateAvailabilityByBooleanOrRoles } from '@pages/QuoteDetails/helpers';

export function getAttributeTypes({
  namedInsuredItem,
  policyQuoteItem,
  ruleEngine,
  roles,
}: {
  namedInsuredItem: any;
  policyQuoteItem: any;
  ruleEngine: RuleEngine;
  roles: Array<string>;
}) {
  const stateOptions = (ruleEngine.execute(namedInsuredItem.attributeTypes.MailingState.optionValues) ?? []) as {
    value: string;
    label: string;
  }[];

  const billPlanOptions = (ruleEngine.execute(policyQuoteItem.attributeTypes.PaymentPlan.optionValues) ?? []) as {
    value: string;
    label: string;
  }[];

  const endorsementBillingOptions = (ruleEngine.execute(
    policyQuoteItem.attributeTypes.EndorsementPaymentOption.optionValues,
  ) ?? []) as { value: string; label: string }[];

  return {
    street: {
      key: 'street',
      ...policyQuoteItem.attributeTypes.BillingStreet,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingStreet?.isVisible),
    },
    street2: {
      key: 'street2',
      ...policyQuoteItem.attributeTypes.BillingStreet2,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingStreet2?.isVisible),
    },
    city: {
      key: 'city',
      ...policyQuoteItem.attributeTypes.BillingCity,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingCity?.isVisible),
    },
    state: {
      key: 'state',
      options: stateOptions,
      ...policyQuoteItem.attributeTypes.BillingState,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingState?.isVisible),
    },
    zip: {
      key: 'zip',
      ...policyQuoteItem.attributeTypes.BillingZip,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingZip?.isVisible),
    },
    primaryContact: {
      key: 'primaryContact',
      ...policyQuoteItem.attributeTypes.BillingContact,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingContact?.isVisible),
    },
    emailAddress: {
      key: 'emailAddress',
      ...policyQuoteItem.attributeTypes.BillingEmail,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingEmail?.isVisible),
    },
    phone: {
      key: 'phone',
      ...policyQuoteItem.attributeTypes.BillingPhone,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingPhone?.isVisible),
    },
    billPlan: {
      key: 'billPlan',
      options: billPlanOptions,
      ...policyQuoteItem.attributeTypes.PaymentPlan,
      isVisible: validateAvailabilityByBooleanOrRoles(
        ruleEngine.execute(policyQuoteItem.attributeTypes.PaymentPlan?.isVisible) as boolean | string[],
        roles,
      ),
    },
    endorsementPaymentOption: {
      key: 'endorsementPaymentOption',
      options: endorsementBillingOptions,
      ...policyQuoteItem.attributeTypes.EndorsementPaymentOption,
      isVisible: validateAvailabilityByBooleanOrRoles(
        ruleEngine.execute(policyQuoteItem.attributeTypes.EndorsementPaymentOption?.isVisible) as boolean | string[],
        roles,
      ),
    },
    binderInd: {
      key: 'binderInd',
      ...(policyQuoteItem.attributeTypes.BinderInd ?? {}),
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BinderInd?.isVisible),
    },
    bindingConditionsDetails: {
      key: 'bindingConditionsDetails',
      ...(policyQuoteItem.attributeTypes.BindingConditionsDetails ?? {}),
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BindingConditionsDetails?.isVisible),
    },
    billingMethod: {
      key: 'billingMethod',
      ...(policyQuoteItem.attributeTypes.BillingMethod ?? {}),
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.BillingMethod?.isVisible),
      options: (ruleEngine.execute(policyQuoteItem.attributeTypes.BillingMethod?.optionValues) ?? []) as {
        value: string;
        label: string;
      }[],
    },
    mailingInternationalAddressInd: {
      key: 'mailingInternationalAddressInd',
      ...(namedInsuredItem.attributeTypes.MailingInternationalAddressInd ?? {}),
      isVisible: !!ruleEngine.execute(namedInsuredItem.attributeTypes.MailingInternationalAddressInd?.isVisible),
    },
  };
}

export const getExcessAttributeTypes = ({
  policyQuoteItem,
  ruleEngine,
  quoteItemId,
  showExcessPremiumPanel,
}: {
  policyQuoteItem: any;
  ruleEngine: RuleEngine;
  quoteItemId: string;
  showExcessPremiumPanel?: boolean;
}): Record<string, any> | null => {
  let fields: Record<string, any> = {
    EXEachEventLimit: {
      ...policyQuoteItem.attributeTypes.EXEachEventLimit,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.EXEachEventLimit?.isVisible),
    },
    EXAggregateLimit: {
      ...policyQuoteItem.attributeTypes.EXAggregateLimit,
    },
    EXPolicyTerrorismCoverageInd: {
      ...policyQuoteItem.attributeTypes.EXPolicyTerrorismCoverageInd,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.EXPolicyTerrorismCoverageInd?.isVisible),
    },
    EXEstimatedPremium1MSelectedLimit: {
      ...policyQuoteItem.attributeTypes.EXEstimatedPremium1MSelectedLimit,
      isActive: !!ruleEngine.execute(policyQuoteItem.attributeTypes.EXEstimatedPremium1MSelectedLimit?.isActive),
    },
    EXEstimatedPremium2MSelectedLimit: {
      ...policyQuoteItem.attributeTypes.EXEstimatedPremium2MSelectedLimit,
      isActive: !!ruleEngine.execute(policyQuoteItem.attributeTypes.EXEstimatedPremium2MSelectedLimit?.isActive),
    },
    EXEstimatedPremium3MSelectedLimit: {
      ...policyQuoteItem.attributeTypes.EXEstimatedPremium3MSelectedLimit,
      isActive: !!ruleEngine.execute(policyQuoteItem.attributeTypes.EXEstimatedPremium3MSelectedLimit?.isActive),
    },
    EXEstimatedPremium4MSelectedLimit: {
      ...policyQuoteItem.attributeTypes.EXEstimatedPremium4MSelectedLimit,
      isActive: !!ruleEngine.execute(policyQuoteItem.attributeTypes.EXEstimatedPremium4MSelectedLimit?.isActive),
    },
    EXEstimatedPremium5MSelectedLimit: {
      ...policyQuoteItem.attributeTypes.EXEstimatedPremium5MSelectedLimit,
      isActive: !!ruleEngine.execute(policyQuoteItem.attributeTypes.EXEstimatedPremium5MSelectedLimit?.isActive),
    },
    LOIExcessLiabilityInd: {
      ...policyQuoteItem.attributeTypes.LOIExcessLiabilityInd,
      isVisible: !!ruleEngine.execute(policyQuoteItem.attributeTypes.LOIExcessLiabilityInd?.isVisible),
    },
    LOIGeneralLiabilityInd: {
      ...policyQuoteItem.attributeTypes.LOIGeneralLiabilityInd,
    },
  };

  const isExcess = fields?.LOIExcessLiabilityInd?.attribute?.value;

  if (showExcessPremiumPanel && isExcess) {
    fields = {
      ...fields,
      EXEstimatedPremium1MSelectedLimitPrint: {
        ...policyQuoteItem.attributeTypes.EXEstimatedPremium1MSelectedLimitPrint,
        attribute: policyQuoteItem.attributeTypes.EXEstimatedPremium1MSelectedLimitPrint?.attribute
          ? policyQuoteItem.attributeTypes.EXEstimatedPremium1MSelectedLimitPrint?.attribute
          : {
              id: `${quoteItemId}-${policyQuoteItem.attributeTypes.EXEstimatedPremium1MSelectedLimitPrint.id}`,
            },
        defaultValue: ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium1MSelectedLimitPrint?.defaultValue,
        ),
        isVisible: !!ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium1MSelectedLimitPrint?.isVisible,
        ),
      },
      EXEstimatedPremium2MSelectedLimitPrint: {
        ...policyQuoteItem.attributeTypes.EXEstimatedPremium2MSelectedLimitPrint,
        attribute: policyQuoteItem.attributeTypes.EXEstimatedPremium2MSelectedLimitPrint?.attribute
          ? policyQuoteItem.attributeTypes.EXEstimatedPremium2MSelectedLimitPrint?.attribute
          : {
              id: `${quoteItemId}-${policyQuoteItem.attributeTypes.EXEstimatedPremium2MSelectedLimitPrint.id}`,
            },
        defaultValue: ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium2MSelectedLimitPrint?.defaultValue,
        ),
        isVisible: !!ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium2MSelectedLimitPrint?.isVisible,
        ),
      },
      EXEstimatedPremium3MSelectedLimitPrint: {
        ...policyQuoteItem.attributeTypes.EXEstimatedPremium3MSelectedLimitPrint,
        attribute: policyQuoteItem.attributeTypes.EXEstimatedPremium3MSelectedLimitPrint?.attribute
          ? policyQuoteItem.attributeTypes.EXEstimatedPremium3MSelectedLimitPrint?.attribute
          : {
              id: `${quoteItemId}-${policyQuoteItem.attributeTypes.EXEstimatedPremium3MSelectedLimitPrint.id}`,
            },
        defaultValue: ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium3MSelectedLimitPrint?.defaultValue,
        ),
        isVisible: !!ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium3MSelectedLimitPrint?.isVisible,
        ),
      },
      EXEstimatedPremium4MSelectedLimitPrint: {
        ...policyQuoteItem.attributeTypes.EXEstimatedPremium4MSelectedLimitPrint,
        attribute: policyQuoteItem.attributeTypes.EXEstimatedPremium4MSelectedLimitPrint?.attribute
          ? policyQuoteItem.attributeTypes.EXEstimatedPremium4MSelectedLimitPrint?.attribute
          : {
              id: `${quoteItemId}-${policyQuoteItem.attributeTypes.EXEstimatedPremium4MSelectedLimitPrint.id}`,
            },
        defaultValue: ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium4MSelectedLimitPrint?.defaultValue,
        ),
        isVisible: !!ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium4MSelectedLimitPrint?.isVisible,
        ),
      },
      EXEstimatedPremium5MSelectedLimitPrint: {
        ...policyQuoteItem.attributeTypes.EXEstimatedPremium5MSelectedLimitPrint,
        attribute: policyQuoteItem.attributeTypes.EXEstimatedPremium5MSelectedLimitPrint?.attribute
          ? policyQuoteItem.attributeTypes.EXEstimatedPremium5MSelectedLimitPrint?.attribute
          : {
              id: `${quoteItemId}-${policyQuoteItem.attributeTypes.EXEstimatedPremium5MSelectedLimitPrint.id}`,
            },
        defaultValue: ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium5MSelectedLimitPrint?.defaultValue,
        ),
        isVisible: !!ruleEngine.execute(
          policyQuoteItem.attributeTypes.EXEstimatedPremium5MSelectedLimitPrint?.isVisible,
        ),
      },
    };
  }

  return fields;
};
